import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle, ShareNetwork } from '@phosphor-icons/react';

const CINZEL = "'Cinzel', serif";
const TICKET_NUMBER_DIGITS = 4;   // zero-pad ticket number for display
const ROLL_DURATION_MS = 1400;     // slot-machine roll
const HOLD_AFTER_REVEAL_MS = 2800; // how long the user reads the card
const EXIT_DURATION_MS = 700;

/**
 * Luxury ticket reveal animation, presented on a 1200×630-style OG card surface.
 *
 * Sequence:
 *   1. dark backdrop fades in
 *   2. card flies in from below + rotates upright (spring)
 *   3. slot-machine digits cycle, then snap to the REAL ticket number
 *   4. ticket code reveals letter-by-letter (typewriter)
 *   5. holds for ~2.8s so the buyer can screenshot / share
 *   6. card scales down + fades out
 *
 * Real data shown:
 *   - ticketNumber (1..2000) — exact entry number that RNG can draw
 *   - ticketCode    — first 8 chars of the ticket UUID (provided by backend)
 *   - drawNumber    — current draw
 */
const TicketRevealCard = ({ open, data, onClose }) => {
  const [rolledNumber, setRolledNumber] = useState(0);
  const [codeRevealed, setCodeRevealed] = useState('');

  const ticketNumber = data?.ticketNumber ?? 0;
  const ticketCode = (data?.ticketCode || '').toUpperCase();
  const drawNumber = data?.drawNumber ?? 1;

  // ── Slot-machine roll for the ticket number ──────────────────────────────
  useEffect(() => {
    if (!open || !ticketNumber) {
      setRolledNumber(0);
      return undefined;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / ROLL_DURATION_MS);
      // ease-out cubic; in last 10% snap to real value
      const eased = 1 - Math.pow(1 - t, 3);
      if (t >= 1) {
        setRolledNumber(ticketNumber);
        return;
      }
      // pseudo-random fast cycling that gradually slows
      const noise = Math.floor(Math.random() * 9999);
      const target = Math.floor(eased * ticketNumber);
      setRolledNumber(t < 0.9 ? noise : target);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open, ticketNumber]);

  // ── Typewriter reveal for the ticket code ────────────────────────────────
  useEffect(() => {
    if (!open || !ticketCode) {
      setCodeRevealed('');
      return undefined;
    }
    const delays = [];
    setCodeRevealed('');
    // Start typing after the number snaps (≈ ROLL_DURATION_MS)
    for (let i = 0; i < ticketCode.length; i += 1) {
      delays.push(setTimeout(() => {
        setCodeRevealed((prev) => prev + ticketCode[i]);
      }, ROLL_DURATION_MS + i * 70));
    }
    return () => delays.forEach(clearTimeout);
  }, [open, ticketCode]);

  // ── Auto close ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    const totalReveal = ROLL_DURATION_MS + ticketCode.length * 70;
    const closeTimer = setTimeout(() => onClose?.(), totalReveal + HOLD_AFTER_REVEAL_MS);
    return () => clearTimeout(closeTimer);
  }, [open, ticketCode.length, onClose]);

  return (
    <AnimatePresence>
      {open && data && (
        <motion.div
          key="ticket-reveal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: EXIT_DURATION_MS / 1000 } }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4"
          data-testid="ticket-reveal-overlay"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-black/75 backdrop-blur-md" />

          <motion.div
            className="relative z-10 w-full max-w-[640px]"
            initial={{ scale: 0.4, rotate: -10, y: 240, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, y: 0, opacity: 1 }}
            exit={{ scale: 0.8, y: -120, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 110, damping: 16, mass: 0.9 }}
          >
            {/* Outer gold border (luxury frame) */}
            <div
              className="rounded-2xl p-[2px]"
              style={{
                background: 'linear-gradient(135deg, #e6be4b 0%, #cba135 35%, #6e5a1e 50%, #cba135 65%, #e6be4b 100%)',
                boxShadow: '0 0 80px rgba(203,161,53,0.45), 0 24px 48px rgba(0,0,0,0.5)',
              }}
            >
              <div className="relative rounded-2xl overflow-hidden bg-[#0e0b04] px-6 sm:px-9 py-7 sm:py-9">
                {/* Inner hairline frame */}
                <div className="absolute inset-2 rounded-xl border border-[#cba135]/15 pointer-events-none" />

                {/* Header row */}
                <div className="flex items-center justify-between relative z-10">
                  <div className="flex items-center gap-3">
                    <img
                      src="/brand/logo-mark-128.png"
                      alt="1:2000 Club"
                      className="h-9 w-auto drop-shadow-[0_2px_6px_rgba(203,161,53,0.4)]"
                      draggable="false"
                    />
                    <div className="flex flex-col leading-none">
                      <span style={{ fontFamily: CINZEL, fontWeight: 600, fontSize: '15px', letterSpacing: '1.2px' }} className="text-[#e6be4b]">1:2000</span>
                      <span style={{ fontFamily: CINZEL, fontWeight: 400, fontSize: '9px', letterSpacing: '5px' }} className="text-[#cba135] mt-1">CLUB</span>
                    </div>
                  </div>
                  <span
                    style={{ fontFamily: CINZEL, fontWeight: 500, fontSize: '11px', letterSpacing: '2.8px' }}
                    className="uppercase text-[#cba135]/70"
                  >
                    Ticket Confirmed
                  </span>
                </div>

                {/* Big ticket number */}
                <div className="mt-7 sm:mt-8 relative z-10">
                  <p
                    style={{ fontFamily: CINZEL, fontSize: '10px', letterSpacing: '4px' }}
                    className="uppercase text-[#cba135]/55 mb-2"
                  >
                    Your Entry
                  </p>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35, duration: 0.5 }}
                    className="text-[#e6be4b] tabular-nums"
                    style={{ fontFamily: CINZEL, fontWeight: 700, fontSize: 'clamp(60px, 12vw, 96px)', letterSpacing: '2px', lineHeight: 1 }}
                    data-testid="ticket-reveal-number"
                  >
                    #{String(rolledNumber).padStart(TICKET_NUMBER_DIGITS, '0')}
                  </motion.p>
                </div>

                {/* Gold rule */}
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 1.3, duration: 0.6 }}
                  className="mt-5 h-[1px] bg-gradient-to-r from-transparent via-[#cba135] to-transparent origin-left"
                />

                {/* Ticket code + Draw + Entry meta */}
                <div className="mt-5 flex flex-wrap items-end justify-between gap-3 relative z-10">
                  <div>
                    <p style={{ fontFamily: CINZEL, fontSize: '9px', letterSpacing: '3px' }} className="uppercase text-[#cba135]/55 mb-1.5">
                      Verification Code
                    </p>
                    <p
                      data-testid="ticket-reveal-code"
                      style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '20px', letterSpacing: '4px' }}
                      className="text-[#cba135] font-semibold tabular-nums"
                    >
                      {codeRevealed || '·'}
                      <motion.span
                        animate={{ opacity: [1, 0, 1] }}
                        transition={{ duration: 0.9, repeat: Infinity }}
                        className="text-[#e6be4b]"
                        style={{ display: codeRevealed.length < ticketCode.length ? 'inline' : 'none' }}
                      >
                        ▍
                      </motion.span>
                    </p>
                  </div>

                  <div className="flex items-end gap-7">
                    <div className="text-right">
                      <p style={{ fontFamily: CINZEL, fontSize: '9px', letterSpacing: '3px' }} className="uppercase text-[#cba135]/55 mb-1">Draw</p>
                      <p style={{ fontFamily: CINZEL, fontWeight: 600, fontSize: '20px' }} className="text-[#cba135] tabular-nums">
                        #{drawNumber}
                      </p>
                    </div>
                    <div className="text-right">
                      <p style={{ fontFamily: CINZEL, fontSize: '9px', letterSpacing: '3px' }} className="uppercase text-[#cba135]/55 mb-1">Entry</p>
                      <p style={{ fontFamily: CINZEL, fontWeight: 600, fontSize: '20px' }} className="text-[#cba135]">
                        €150
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pulse-grid dot row (visual nod to RNG) */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2.0, duration: 0.5 }}
                  className="mt-7 flex items-center justify-between gap-1 relative z-10"
                >
                  {Array.from({ length: 22 }).map((_, i) => {
                    const id = `dot-${i}`;
                    const highlight = [4, 9, 14, 18].includes(i);
                    return (
                      <span
                        key={id}
                        className={`h-[3px] w-[3px] rounded-full ${highlight ? 'bg-[#e6be4b]' : 'bg-[#cba135]/30'}`}
                        style={highlight ? { boxShadow: '0 0 6px rgba(230,190,75,0.7)' } : undefined}
                      />
                    );
                  })}
                </motion.div>

                {/* "ENTERED INTO DRAW" footer with check */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2.2, duration: 0.45 }}
                  className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 relative z-10"
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle size={18} weight="duotone" className="text-[#e6be4b]" />
                    <span style={{ fontFamily: CINZEL, fontWeight: 500, fontSize: '11px', letterSpacing: '3.5px' }} className="uppercase text-[#cba135]">
                      Entered into Draw — Good Luck
                    </span>
                  </div>
                  <ShareButton ticketNumber={ticketNumber} drawNumber={drawNumber} />
                </motion.div>
              </div>
            </div>

            {/* Click-to-dismiss hint */}
            <p className="text-center text-[10px] text-[#cba135]/40 mt-4 uppercase tracking-[0.3em]">
              tap to dismiss
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TicketRevealCard;

// ── Web Share API integration ────────────────────────────────────────────────
const ShareButton = ({ ticketNumber, drawNumber }) => {
  const [copied, setCopied] = useState(false);
  const padded = String(ticketNumber).padStart(4, '0');

  const handleShare = async (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/?utm_source=share&utm_medium=ticket&utm_campaign=draw${drawNumber}`;
    const text = `I just locked Ticket #${padded} in 1:2000 Club Draw #${drawNumber} 🍀 — only 2,000 tickets, €200,000 prize. Join me:`;
    if (navigator.share) {
      try {
        await navigator.share({ title: '1:2000 Club', text, url });
      } catch {
        // user cancelled, no-op
      }
      return;
    }
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignore — older browsers without clipboard either
    }
  };

  return (
    <button
      type="button"
      onClick={handleShare}
      data-testid="ticket-share-button"
      className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 bg-[#cba135]/10 border border-[#cba135]/40 hover:bg-[#cba135]/20 hover:border-[#cba135] transition-colors"
    >
      <ShareNetwork size={14} weight="bold" className="text-[#e6be4b]" />
      <span
        style={{ fontFamily: CINZEL, fontWeight: 500, fontSize: '10px', letterSpacing: '3px' }}
        className="uppercase text-[#cba135]"
      >
        {copied ? 'Link copied' : 'Share'}
      </span>
    </button>
  );
};
